import React, { useState } from 'react';
import { format } from 'date-fns';
import { HiX, HiTrash, HiClock, HiLocationMarker, HiUserCircle, HiCurrencyDollar, HiDocumentText } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { updateInstanceOverride } from '@coparent/shared/src/services/activityService';
import { formatCurrency } from '../../utils/expenseUtils';
import ChildDots from '../common/ChildDots';
import LinkifiedText from '../common/LinkifiedText';
import AttachmentView from '../common/AttachmentView';
import { useAuth } from '../../context/AuthContext';

const ActivityInstanceModal = ({ event, family, familyId, childrenMap, allParticipants, onClose, onSkip, readOnly = false }) => {
  const { currentUser } = useAuth();
  const res = event.resource;
  const [dropoff, setDropoff] = useState(res.dropoffParentId || 'custody');
  const [pickup, setPickup] = useState(res.pickupParentId || 'custody');
  const [saving, setSaving] = useState(false);

  const hasChanges = dropoff !== (res.dropoffParentId || 'custody') || pickup !== (res.pickupParentId || 'custody');

  const resolveParentLabel = (id, selfLabel) => {
    if (!id) return 'Custody parent';
    if (id === 'self') return selfLabel;
    const p = allParticipants?.find(x => x.id === id);
    if (!p) return 'Unknown';
    return `${p.displayName}${p.role === 'household_member' ? ' (household)' : ''}`;
  };
  const dropoffLabel = resolveParentLabel(res.dropoffParentId, 'Self arrival (no drop-off)');
  const pickupLabel = resolveParentLabel(res.pickupParentId, 'Self dismissal (no pick-up)');

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateInstanceOverride(familyId, res.activityId, res.date, {
        dropoffParentId: dropoff === 'custody' ? null : dropoff,
        pickupParentId: pickup === 'custody' ? null : pickup,
      }, currentUser?.uid || null);
      toast.success('Instance updated');
      onClose();
    } catch (error) {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-bold text-ink-900 text-base">{res.activityName}</h3>
            {res.hasInstanceOverride && (
              <span className="text-[0.625rem] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mt-1 inline-block">
                Modified
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:bg-surface-100 hover:text-ink-600 transition-colors"
          >
            <HiX className="text-base" />
          </button>
        </div>

        <div className="space-y-2 mb-5">
          <p className="text-sm text-ink-500 font-medium">
            {format(event.start, 'EEEE, MMMM d, yyyy')}
          </p>
          {res.startTime && (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <HiClock className="text-ink-300 text-xs" />
              <span className="font-medium">
                {res.startTime}
                {res.endTime && ` – ${res.endTime}`}
              </span>
            </div>
          )}
          {res.location && (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <HiLocationMarker className="text-ink-300 text-xs" />
              <span className="font-medium">{res.location}</span>
            </div>
          )}
          {readOnly && family && (
            <>
              <div className="flex items-center gap-2 text-sm text-ink-500">
                <HiUserCircle className="text-ink-300 text-xs" />
                <span className="font-medium">Drop-off: {dropoffLabel}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-ink-500">
                <HiUserCircle className="text-ink-300 text-xs" />
                <span className="font-medium">Pick-up: {pickupLabel}</span>
              </div>
            </>
          )}
          {res.notes && (
            <div className="flex items-start gap-2 text-sm text-ink-500">
              <HiDocumentText className="text-ink-300 text-xs mt-1 flex-shrink-0" />
              <LinkifiedText text={res.notes} className="font-medium" />
            </div>
          )}
          {res.cost && (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <HiCurrencyDollar className="text-ink-300 text-xs" />
              <span className="font-medium">{formatCurrency(res.cost)}/session</span>
            </div>
          )}
          {res.childIds?.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <span className="text-ink-300 text-xs font-medium">For</span>
              <ChildDots childIds={res.childIds} childrenMap={childrenMap} showNames />
            </div>
          )}
          {res.attachments?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-ink-400 uppercase tracking-wide mb-2">Attachments</p>
              <div className="flex flex-wrap gap-3">
                {res.attachments.map((att, i) => (
                  <AttachmentView key={att.storagePath || `att-${i}`} attachment={att} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Drop-off / Pick-up editing */}
        {family && !readOnly && (
          <div className="space-y-3 mb-5">
            <p className="text-xs font-semibold text-ink-400 uppercase tracking-wide flex items-center gap-1.5">
              <HiUserCircle className="text-sm" />
              Responsible parent — this date only
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5">Drop-off</label>
                <select
                  value={dropoff}
                  onChange={e => setDropoff(e.target.value)}
                  className="input-base text-sm"
                >
                  <option value="custody">Custody parent</option>
                  {allParticipants.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.role === 'household_member' ? ' (household)' : ''}
                    </option>
                  ))}
                  <option value="self">Self arrival (no drop-off)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5">Pick-up</label>
                <select
                  value={pickup}
                  onChange={e => setPickup(e.target.value)}
                  className="input-base text-sm"
                >
                  <option value="custody">Custody parent</option>
                  {allParticipants.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.role === 'household_member' ? ' (household)' : ''}
                    </option>
                  ))}
                  <option value="self">Self dismissal (no pick-up)</option>
                </select>
              </div>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary w-full text-sm"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        )}

        {!readOnly && (
          <div className="border-t border-surface-100 pt-4">
            <button
              onClick={onSkip}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <HiTrash className="text-sm" />
              Remove this instance
            </button>
            {res.isRecurring && (
              <p className="text-xs text-ink-300 text-center mt-2">
                Only removes this date — the rest of the series stays
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityInstanceModal;
